// @react
import React, { useState, useContext, ReactElement } from 'react'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @common
import { SnackbarContext } from 'modules/common/context/snackbar'
// @graphql
import { useMutation, useQuery } from '@apollo/client'
import {
  GetPublicUsersQuery,
  GetPublicUsersQueryVariables,
  UserFollowUserMutation,
  UserFollowUserMutationVariables,
  UserSearchItem,
  UserUnfollowUserMutation,
  UserUnfollowUserMutationVariables,
  PublicUserConnectionType,
} from 'lib/graphql/graphqlTypes'
import client from 'graphql/Apollo/apolloClient'
import { GET_PUBLIC_USERS } from 'graphql/queries/userNetwork.query'
import {
  USER_FOLLOW_USER,
  USER_UNFOLLOW_USER,
} from 'graphql/mutations/userNetwork.mutation'
// @libraries
import clsx from 'clsx'
import { Typography } from '@material-ui/core'
// @utils
import Helper from 'utils/Helper.class'
// @components
import Grid from '@bit/atd.web.grid'
import WidgetFollowCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/WidgetFollow'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
import Box from '@bit/atd.web.box'
// @module components
import QueryError from 'modules/common/components/Errors/QueryError'
import useResizeObserver from 'use-resize-observer'
import { useStyles } from './style'

const RecommendedUsersCard = (): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [isSubmitting, setIsMutationSubmitting] = useState<string | null>(null)
  const [isLayout4, setIsLayout4] = useState(false)

  /******************************************
   * HOOKS
   ******************************************/

  const { ref } = useResizeObserver<HTMLDivElement>({
    onResize: ({ width }) => {
      if (width) {
        setIsLayout4(width > 450)
      }
    },
  })

  const classes = useStyles()
  const snackbar = useContext(SnackbarContext)

  /******************************************
   * GQL HOOKS
   ******************************************/
  const queryVars: GetPublicUsersQueryVariables = {
    input: {
      filters: {
        type: PublicUserConnectionType.RECOMMENDED,
      },
      page: 1,
      pageSize: 4,
    },
  }

  const { loading: isLoading, error: isError, data } = useQuery<
    GetPublicUsersQuery,
    GetPublicUsersQueryVariables
  >(GET_PUBLIC_USERS, {
    variables: queryVars,
  })

  const [followUser] = useMutation<
    UserFollowUserMutation,
    UserFollowUserMutationVariables
  >(USER_FOLLOW_USER)

  const [unfollowUser] = useMutation<
    UserUnfollowUserMutation,
    UserUnfollowUserMutationVariables
  >(USER_UNFOLLOW_USER)

  const publicUsersCount = data?.publicUsers?.meta?.count || 0

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   * @param item
   */
  const followUserAction = async (item: UserSearchItem) => {
    setIsMutationSubmitting(item.id)
    const { errors: mutationErrors } = await followUser({
      variables: {
        input: {
          userId: item.id,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: `Successfully followed ${item.firstName}`,
        show: true,
      })

      client.cache.modify({
        id: client.cache.identify(item),
        fields: {
          isFollowedByCurrentUser() {
            return true
          },
        },
      })
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: `There was a problem following ${item.firstName}`,
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /**
   *
   * @param item
   */
  const unfollowUserAction = async (item: UserSearchItem) => {
    const { errors: mutationErrors } = await unfollowUser({
      variables: {
        input: {
          userId: item.id,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: `Successfully unfollowed ${item.firstName}`,
        show: true,
      })

      client.cache.modify({
        id: client.cache.identify(item),
        fields: {
          isFollowedByCurrentUser() {
            return false
          },
        },
      })
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: `There was a problem unfollowing ${item.firstName}`,
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const publicUsersPageSize = 4

  const renderSkelContent = () => (
    <Grid className={classes.skeletonWrapper} container>
      {Array(1)
        .fill('')
        .map((item, index) => {
          return (
            <Grid key={`ske_loader_${index}`} xs={12} item>
              <SkeletonLoader />
            </Grid>
          )
        })}
    </Grid>
  )

  const renderUsersList = () =>
    data?.publicUsers?.data && data?.publicUsers?.data.length ? (
      <Grid
        className={clsx(classes.cardsWrapper, {
          [classes.isLayout4]: isLayout4,
        })}
        container
      >
        {data?.publicUsers?.data.map((item) => {
          const followCTAText = item?.isFollowedByCurrentUser
            ? 'Unfollow'
            : 'Follow'
          const followCTACallback = item?.isFollowedByCurrentUser
            ? unfollowUserAction
            : followUserAction

          return (
            <Grid key={`${item.id}`} xs={12} sm={12} item>
              <ConditionalLoader condition={isSubmitting !== item.id}>
                <WidgetFollowCard
                  avatar={{
                    size: 48,
                    src: item?.imageUrl as string,
                    alt: `${item?.firstName} ${item?.lastName}`,
                    bgColor: Helper.randomColorChooser(),
                    textAs: (
                      <p>
                        {Helper.getAcronymFromSentence(
                          `${item?.firstName} ${item?.lastName}`,
                        )}
                      </p>
                    ),
                  }}
                  title={`${item?.firstName} ${item?.lastName}`}
                  subtitle={item?.jobTitle || ''}
                  btnLabel={followCTAText}
                  titleTargetUrl="_blank"
                  titleURL={item.url}
                  // @ts-ignore
                  onButtonClick={() => followCTACallback(item)}
                  following={item?.isFollowedByCurrentUser}
                />
              </ConditionalLoader>
            </Grid>
          )
        })}
      </Grid>
    ) : (
      <div>There are no results!</div>
    )

  return (
    <Box
      classNames={{ header: classes.cardHeader, body: classes.cardBody }}
      variant="subModule"
      header={{
        title: 'Build Your Network',
        subtitle: (
          <>
            <Typography className={classes.textBase} component={'p'}>
              Connect with leaders in the industry, professionals with a role
              similar to yours, and thousands of other ATD users.
            </Typography>
            <Typography className={classes.textSmall} component={'p'}>
              Here are some suggestions for people to follow:
            </Typography>
          </>
        ),
      }}
      footer={{
        link: {
          url: `${Helper.getPathFromRouteKey('PROFILE_NETWORK')}?tab=3`,
          label:
            publicUsersCount > publicUsersPageSize
              ? 'View all Suggestions'
              : '',
        },
      }}
    >
      <>
        <div ref={ref}>
          {isLoading ? renderSkelContent() : renderUsersList()}
        </div>
      </>
    </Box>
  )
}

export default RecommendedUsersCard
